import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { useConfigStore } from '../../stores';
import { getAuthClient } from '../../utils/auth';
import { buildYourTourApiUrl } from '../../utils/ApiHelpers';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import OnImageDropdown from '../OnImageDropdown/OnImageDropdown';
import Player from '../Player/Player';

import './VirtualTour.scss';

const auth = getAuthClient();

const isYouTubeUrl = (url) => /(?:youtube\.com|youtu\.be)/.test(url);

const VIRTUAL_TOUR_INSTRUCTIONS = {
  TruPlace: 'Click and drag to look around. For additional help, refer to TruPlace documentation.',
  '3D Plans': "Click and drag to explore room. Scroll to zoom in and out. Click the Floor Plan icon to see Bird's Eye View, then click a room to fly into a walking tour.",
};

const VirtualTourDetail = ({
  id,
  uuid,
  threeSixtiesData,
  className,
  isLoading,
  showInstructions,
}) => {
  // Check that we have everything we need to proceed, unless we're in a loading state
  if ((!threeSixtiesData || threeSixtiesData.length === 0) && !uuid && !isLoading) return null;

  const [threeSixties, setThreeSixties] = useState(null);
  const [options, setOptions] = useState(null);
  const [activeVtPath, setActiveVtPath] = useState(null);
  const [activeType, setActiveType] = useState(null);

  const config = useConfigStore((state) => state.config);

  // if this component is being loaded by a place, unit, or floor plan detail,
  // we already have the threesixties and dont need to get them from the api
  useEffect(() => {
    if (threeSixtiesData) {
      setThreeSixties(threeSixtiesData);
    }
  }, [threeSixtiesData]);

  useEffect(() => {
    if (uuid) {
      let isMounted = true;
      const fetchData = async () => {
        const url = buildYourTourApiUrl(`/your_tour_api/virtual_tour/${uuid}`);
        const data = await auth.fetchWithAuthentication(url, 'GET', null);
        if (isMounted && data && !data.error) {
          setThreeSixties(data);
        }
      };
      fetchData();
      return () => {
        isMounted = false;
      };
    }
  }, [uuid]);

  // loop through three sixties to get btns and xml info
  useEffect(() => {
    if (threeSixties) {
      const rawOptions = [];

      threeSixties.forEach(
        (nodeThreeSixty, index) => {
          const path = nodeThreeSixty.type === '3D Plans' ? `${nodeThreeSixty.url}&infopanel=false` : nodeThreeSixty.url;
          rawOptions[index] = {
            type: nodeThreeSixty.type,
            id: nodeThreeSixty.uuid,
            text: nodeThreeSixty.buttonText,
            isDefault: index === 0,
            path,
          };
        },
      );

      setOptions(rawOptions);
    }
  }, [threeSixties]);

  // set initial vt path
  useEffect(() => {
    if (options) {
      options.forEach(
        (option) => {
          if (option.isDefault) {
            setActiveVtPath(option.path);
            setActiveType(option.type);
          }
        },
      );
    }
  }, [options]);

  // handle option click
  const handleOptionOnClick = (option) => {
    setActiveVtPath(option.path);
    setActiveType(option.type);
  };

  return (
    <div id={id || ''} className={className || ''}>
      <div className={`virtualTour virtualTour--${activeType}`}>

        {config && config.primaryColor && activeType !== 'TruPlace'
          && <LoadingSpinner />}

        {activeVtPath && isYouTubeUrl(activeVtPath)
          && (
            <Player
              url={activeVtPath}
              className="virtualTour__player"
              muted
              autoplay={options?.length > 1 && !isYouTubeUrl(options[0].path)}
            />
          )}

        {activeVtPath && !isYouTubeUrl(activeVtPath)
          && (
            <iframe
              title="Virtual Tour"
              className={`virtualTour__iframe virtualTour__iframe--${activeType}`}
              src={activeVtPath}
            />
          )}

        {options && options.length > 1
          && (
            <OnImageDropdown
              className={`virtualTour__dropdown virtualTour__dropdown--${activeType}`}
              optionsLocation="left"
              closeDropdownOnClick
              options={options}
              handleOptionOnClick={(option) => handleOptionOnClick(option)}
            />
          )}

      </div>

      {showInstructions && activeType && VIRTUAL_TOUR_INSTRUCTIONS[activeType] && (
        <div className="detail__footnote">
          {VIRTUAL_TOUR_INSTRUCTIONS[activeType]}
        </div>
      )}
    </div>
  );
};

VirtualTourDetail.propTypes = {
  id: PropTypes.string,
  uuid: PropTypes.string,
  threeSixtiesData: PropTypes.arrayOf(PropTypes.object),
  className: PropTypes.string,
  isLoading: PropTypes.bool,
  showInstructions: PropTypes.bool,
};

VirtualTourDetail.defaultProps = {
  id: null,
  uuid: null,
  threeSixtiesData: null,
  className: null,
  isLoading: false,
  showInstructions: false,
};

export default VirtualTourDetail;
